import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { listSites, createSite, deleteSite, updateSite } from "../api/sites";
import {
  deleteSiteSensorCertificate,
  listSiteSensorInventory,
  uploadSiteSensorCertificate,
  upsertSiteSensorInventory,
} from "../api/sensorInventory";
import SiteForm from "../ui/SiteForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import clsx from "clsx";
const GearIcon = ({ className = "h-4 w-4" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const STATUS_TONE = {
  Valid: "bg-emerald-100 text-emerald-800",
  Expired: "bg-rose-100 text-rose-800",
  "Missing calibration metadata": "bg-amber-100 text-amber-800",
  "Certificate uploaded": "bg-sky-100 text-sky-800",
  "Missing certificate file": "bg-slate-100 text-slate-700",
};

const StatusBadge = ({ label }) => (
  <span className={clsx("inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium", STATUS_TONE[label] || "bg-slate-100 text-slate-700")}>
    {label}
  </span>
);

const normalizeSensorDraft = (item) => ({
  signal_match_id: item.signal_match_id ?? null,
  sensor_role: item.sensor_role || "",
  manufacturer: item.manufacturer || "",
  model: item.model || "",
  sensor_serial_number: item.sensor_serial_number || "",
  asset_tag: item.asset_tag || "",
  measurement_unit: item.measurement_unit || "",
  accuracy_class: item.accuracy_class || "",
  location_note: item.location_note || "",
  calibration_certificate_number: item.calibration_certificate_number || "",
  calibration_provider: item.calibration_provider || "",
  calibration_date: item.calibration_date || "",
  calibration_valid_until: item.calibration_valid_until || "",
  is_active: item.is_active !== false,
  notes: item.notes || "",
});

const normalizeSensorDrafts = (items) =>
  (items || [])
    .map(normalizeSensorDraft)
    .sort((a, b) => Number(a.signal_match_id || 0) - Number(b.signal_match_id || 0));

function SensorsModal({ site, onClose }) {
  const [items, setItems] = useState([]);
  const [savedItems, setSavedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingKey, setSavingKey] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const hasUnsavedChanges = useMemo(
    () => JSON.stringify(normalizeSensorDrafts(items)) !== JSON.stringify(normalizeSensorDrafts(savedItems)),
    [items, savedItems]
  );

  const load = async () => {
    if (!site?.id) return;
    setLoading(true);
    setError("");
    try {
      const data = await listSiteSensorInventory(site.id);
      setItems(data?.items || []);
      setSavedItems(data?.items || []);
    } catch (loadError) {
      setError(loadError?.response?.data?.detail || "Failed to load site sensors.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [site?.id]);

  const updateItem = (signalMatchId, patch) => {
    setItems((current) =>
      current.map((item) => (item.signal_match_id === signalMatchId ? { ...item, ...patch } : item))
    );
  };

  const replaceSavedItem = (updatedItem) => {
    setItems((current) =>
      current.map((item) => (item.signal_match_id === updatedItem.signal_match_id ? updatedItem : item))
    );
    setSavedItems((current) =>
      current.map((item) => (item.signal_match_id === updatedItem.signal_match_id ? updatedItem : item))
    );
  };

  const attemptClose = () => {
    if (savingKey || uploadingKey) {
      setError("Wait for the current save or upload to finish before closing.");
      return;
    }
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        "You have unsaved sensor metadata changes. If you close now, those edits will be lost."
      );
      if (!confirmed) return;
    }
    onClose();
  };

  const saveItem = async (item) => {
    setSavingKey(item.signal_match_id);
    setError("");
    try {
      const saved = await upsertSiteSensorInventory(site.id, item.signal_match_id, {
        sensor_role: item.sensor_role || null,
        manufacturer: item.manufacturer || null,
        model: item.model || null,
        sensor_serial_number: item.sensor_serial_number || null,
        asset_tag: item.asset_tag || null,
        measurement_unit: item.measurement_unit || null,
        accuracy_class: item.accuracy_class || null,
        location_note: item.location_note || null,
        calibration_certificate_number: item.calibration_certificate_number || null,
        calibration_provider: item.calibration_provider || null,
        calibration_date: item.calibration_date || null,
        calibration_valid_until: item.calibration_valid_until || null,
        is_active: item.is_active !== false,
        notes: item.notes || null,
      });
      replaceSavedItem(saved);
      return saved;
    } catch (saveError) {
      setError(saveError?.response?.data?.detail || "Failed to save sensor metadata.");
      throw saveError;
    } finally {
      setSavingKey(null);
    }
  };

  const uploadCertificate = async (item, file) => {
    if (!file) return;
    setUploadingKey(item.signal_match_id);
    setError("");
    try {
      const saved = item.id ? item : await saveItem(item);
      const updated = await uploadSiteSensorCertificate(site.id, saved.id, file);
      replaceSavedItem(updated);
    } catch (uploadError) {
      setError(uploadError?.response?.data?.detail || "Failed to upload certificate.");
    } finally {
      setUploadingKey(null);
    }
  };

  const removeCertificate = async (item) => {
    if (!item.id) return;
    if (!window.confirm("Remove this certificate PDF?")) return;
    setUploadingKey(item.signal_match_id);
    setError("");
    try {
      await deleteSiteSensorCertificate(site.id, item.id);
      await load();
    } catch (deleteError) {
      setError(deleteError?.response?.data?.detail || "Failed to remove certificate.");
    } finally {
      setUploadingKey(null);
    }
  };

  return (
    <Dialog open={Boolean(site)} onOpenChange={(open) => (!open ? attemptClose() : null)}>
      <DialogContent className="max-h-[90vh] w-[min(1200px,96vw)] max-w-none overflow-hidden">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle>Sensors - {site?.name}</DialogTitle>
              <p className="mt-1 text-sm text-slate-500">
                Maintain calibration metadata for IEC PR IRRAD and MODTEMP sensors. Source mappings stay managed in Signal Match.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={attemptClose}
                className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={attemptClose}
                aria-label="Close sensors dialog"
                className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-slate-300 text-xl font-medium text-slate-600 hover:bg-slate-50"
              >
                ×
              </button>
            </div>
          </div>
        </DialogHeader>
        {error ? <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{error}</div> : null}
        {hasUnsavedChanges ? (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Unsaved changes detected. Click <span className="font-semibold">Save metadata</span> for each edited sensor before closing, or your edits will be lost.
          </div>
        ) : null}
        <div className="max-h-[74vh] overflow-auto pr-1">
          {loading ? <div className="py-10 text-center text-sm text-slate-500">Loading sensors...</div> : null}
          {!loading && !items.length ? <div className="py-10 text-center text-sm text-slate-500">No IRRAD or MODTEMP signals found for this site.</div> : null}
          {!loading ? (
            <div className="space-y-4">
              {items.map((item) => (
                <div key={item.signal_match_id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div className="text-base font-semibold text-slate-900">{item.source_name_snapshot || item.source_name || item.signal_name}</div>
                      <div className="mt-1 text-sm text-slate-500">
                        {item.signal_type_short} signal match #{item.signal_match_id}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <StatusBadge label={item.calibration_status} />
                      <StatusBadge label={item.certificate_status} />
                    </div>
                  </div>

                  <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="font-medium text-slate-700">Sensor role</span>
                      <input className="rounded-md border border-slate-300 px-3 py-2" value={item.sensor_role || ""} onChange={(e) => updateItem(item.signal_match_id, { sensor_role: e.target.value })} />
                    </label>
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="font-medium text-slate-700">Manufacturer</span>
                      <input className="rounded-md border border-slate-300 px-3 py-2" value={item.manufacturer || ""} onChange={(e) => updateItem(item.signal_match_id, { manufacturer: e.target.value })} />
                    </label>
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="font-medium text-slate-700">Model</span>
                      <input className="rounded-md border border-slate-300 px-3 py-2" value={item.model || ""} onChange={(e) => updateItem(item.signal_match_id, { model: e.target.value })} />
                    </label>
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="font-medium text-slate-700">Serial number</span>
                      <input className="rounded-md border border-slate-300 px-3 py-2" value={item.sensor_serial_number || ""} onChange={(e) => updateItem(item.signal_match_id, { sensor_serial_number: e.target.value })} />
                    </label>
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="font-medium text-slate-700">Asset tag</span>
                      <input className="rounded-md border border-slate-300 px-3 py-2" value={item.asset_tag || ""} onChange={(e) => updateItem(item.signal_match_id, { asset_tag: e.target.value })} />
                    </label>
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="font-medium text-slate-700">Measurement unit</span>
                      <input className="rounded-md border border-slate-300 px-3 py-2" value={item.measurement_unit || ""} onChange={(e) => updateItem(item.signal_match_id, { measurement_unit: e.target.value })} />
                    </label>
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="font-medium text-slate-700">Accuracy class</span>
                      <input className="rounded-md border border-slate-300 px-3 py-2" value={item.accuracy_class || ""} onChange={(e) => updateItem(item.signal_match_id, { accuracy_class: e.target.value })} />
                    </label>
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="font-medium text-slate-700">Calibration provider</span>
                      <input className="rounded-md border border-slate-300 px-3 py-2" value={item.calibration_provider || ""} onChange={(e) => updateItem(item.signal_match_id, { calibration_provider: e.target.value })} />
                    </label>
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="font-medium text-slate-700">Certificate number</span>
                      <input className="rounded-md border border-slate-300 px-3 py-2" value={item.calibration_certificate_number || ""} onChange={(e) => updateItem(item.signal_match_id, { calibration_certificate_number: e.target.value })} />
                    </label>
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="font-medium text-slate-700">Calibration date</span>
                      <input className="rounded-md border border-slate-300 px-3 py-2" type="date" value={item.calibration_date || ""} onChange={(e) => updateItem(item.signal_match_id, { calibration_date: e.target.value })} />
                    </label>
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="font-medium text-slate-700">Valid until</span>
                      <input className="rounded-md border border-slate-300 px-3 py-2" type="date" value={item.calibration_valid_until || ""} onChange={(e) => updateItem(item.signal_match_id, { calibration_valid_until: e.target.value })} />
                    </label>
                    <label className="flex items-center gap-2 pt-6 text-sm text-slate-700">
                      <input type="checkbox" checked={item.is_active !== false} onChange={(e) => updateItem(item.signal_match_id, { is_active: e.target.checked })} />
                      Active
                    </label>
                  </div>

                  <div className="mt-4 grid gap-4 lg:grid-cols-2">
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="font-medium text-slate-700">Location note</span>
                      <textarea className="min-h-[92px] rounded-md border border-slate-300 px-3 py-2" value={item.location_note || ""} onChange={(e) => updateItem(item.signal_match_id, { location_note: e.target.value })} />
                    </label>
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="font-medium text-slate-700">Notes</span>
                      <textarea className="min-h-[92px] rounded-md border border-slate-300 px-3 py-2" value={item.notes || ""} onChange={(e) => updateItem(item.signal_match_id, { notes: e.target.value })} />
                    </label>
                  </div>

                  <div className="mt-4 flex flex-col gap-3 rounded-lg bg-slate-50 px-4 py-3 text-sm lg:flex-row lg:items-center lg:justify-between">
                    <div className="space-y-1 text-slate-600">
                      <div>Certificate: {item.certificate_filename || "No PDF uploaded"}</div>
                      {item.certificate_uploaded_at ? <div>Uploaded at: {item.certificate_uploaded_at}</div> : null}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {item.certificate_url ? (
                        <a className="rounded-md border border-slate-300 px-3 py-2 font-medium text-slate-700 hover:bg-white" href={item.certificate_url} target="_blank" rel="noreferrer">
                          Open certificate
                        </a>
                      ) : null}
                      <label className="cursor-pointer rounded-md border border-slate-300 bg-white px-3 py-2 font-medium text-slate-700 hover:bg-slate-100">
                        {uploadingKey === item.signal_match_id ? "Uploading..." : "Upload PDF"}
                        <input
                          type="file"
                          accept="application/pdf,.pdf"
                          className="hidden"
                          disabled={uploadingKey === item.signal_match_id}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            e.target.value = "";
                            uploadCertificate(item, file);
                          }}
                        />
                      </label>
                      <button
                        type="button"
                        disabled={!item.certificate_available || uploadingKey === item.signal_match_id}
                        onClick={() => removeCertificate(item)}
                        className={clsx(
                          "rounded-md border px-3 py-2 font-medium",
                          item.certificate_available
                            ? "border-rose-300 text-rose-700 hover:bg-rose-50"
                            : "cursor-not-allowed border-slate-200 text-slate-400"
                        )}
                      >
                        Remove PDF
                      </button>
                      <button
                        type="button"
                        disabled={savingKey === item.signal_match_id}
                        onClick={() => saveItem(item)}
                        className="rounded-md bg-blue-600 px-3 py-2 font-medium text-white hover:bg-blue-700 disabled:bg-slate-300"
                      >
                        {savingKey === item.signal_match_id ? "Saving..." : "Save metadata"}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SiteManagement() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [sensorSite, setSensorSite] = useState(null);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const data = await listSites();
      setSites(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredSites = useMemo(() => {
    return (
      sites
        .slice()
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        .filter((s) =>
          s.name?.toLowerCase().includes(search.toLowerCase())
        )
    );
  }, [sites, search]);

  const onCreate = async (values) => {
    await createSite(values);
    setShowForm(false);
    await load();
  };

  const onUpdate = async (id, values) => {
    await updateSite(id, values);
    setEditing(null);
    await load();
  };

  const onDelete = async (id) => {
    if (!id) return;
    if (!window.confirm("Delete this site?")) return;
    await deleteSite(id);
    await load();
    setSelected(null);
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Site Management</h1>
        <p className="text-gray-500">
          Create, update, and remove sites. Manage your solar power plant portfolio here.
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md shadow"
          >
            + Add Site
          </button>

          <button
            onClick={() => setEditing(selected)}
            disabled={!selected}
            className={clsx(
              "px-4 py-2 rounded-md border",
              selected
                ? "bg-amber-500 text-white hover:bg-amber-600"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            )}
          >
            Edit Site
          </button>

          <button
            onClick={() => onDelete(selected?.id)}
            disabled={!selected}
            className={clsx(
              "px-4 py-2 rounded-md border",
              selected
                ? "text-red-600 hover:bg-red-50 border-red-300"
                : "text-gray-400 cursor-not-allowed border-gray-200"
            )}
          >
            Delete
          </button>

          <button
            disabled={!selected}
            onClick={() => navigate(`/maintenance/site-config/${selected.id}`)}
            className={clsx(
              "flex items-center gap-2 px-4 py-2 rounded-md border transition",              
              selected
                ? "bg-emerald-600 text-white hover:bg-emerald-700"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            )}
          >
            <GearIcon className="h-4 w-4" />
            <span>Configure</span>
          </button>

          <button
            disabled={!selected}
            onClick={() => setSensorSite(selected)}
            className={clsx(
              "px-4 py-2 rounded-md border transition",
              selected
                ? "bg-sky-600 text-white hover:bg-sky-700"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            )}
          >
            Sensors
          </button>
        </div>

        <input
          type="text"
          placeholder="Search site..."
          className="border rounded-md px-3 py-2 text-sm w-64"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <div
        className="bg-white rounded-xl shadow border overflow-y-auto"
        style={{ maxHeight: "420px" }}
      >
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 sticky top-0">
            <tr>
              <th className="text-left px-4 py-2">Site ID</th>
              <th className="text-left px-4 py-2">Name</th>
              <th className="text-left px-4 py-2">City</th>
              <th className="text-left px-4 py-2">Country</th>
              <th className="text-left px-4 py-2 text-right">AC (MVA)</th>
              <th className="text-left px-4 py-2 text-right">DC (MWp)</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && filteredSites.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                  No matching sites.
                </td>
              </tr>
            )}
            {!loading &&
              filteredSites.map((s) => (
                <tr
                  key={s.id}
                  onClick={() => setSelected(selected?.id === s.id ? null : s)}
                  className={clsx(
                    "cursor-pointer border-t hover:bg-blue-50 transition",
                    selected?.id === s.id && "bg-blue-100"
                  )}
                >
                  <td className="px-4 py-2">{s.id}</td>
                  <td className="px-4 py-2">{s.name}</td>
                  <td className="px-4 py-2">{s.city || "-"}</td>
                  <td className="px-4 py-2">{s.country || "-"}</td>
                  <td className="px-4 py-2 text-right">
                    {s.ac_nominal_capacity || "-"}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {s.dc_nominal_capacity || "-"}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      {showForm && (
        <SiteForm
          title="Add Site"
          onClose={() => setShowForm(false)}
          onSubmit={onCreate}
          initialValues={{
            name: "",
            address: "",
            city: "",
            zip_code: "",
            country: "",
            use_for_forecasting: true,
            is_tracker: false,
            backtracking_enabled: true,
            tracker_stow_position_deg: null,
            tracker_stow_wind_speed_mps: null,
            tracker_resume_wind_speed_mps: null,
            gps_latitude: null,
            gps_longitude: null,
            cable_losses_site: null,
            irrad_avail: null,
            irrad_pr: null,
          }}
        />
      )}

      {/* Edit modal */}
      {editing && (
        <SiteForm
          title={`Edit: ${editing.name}`}
          onClose={() => setEditing(null)}
          onSubmit={(vals) => onUpdate(editing.id, vals)}
          initialValues={editing}
        />
      )}

      {sensorSite ? <SensorsModal site={sensorSite} onClose={() => setSensorSite(null)} /> : null}
    </div>
  );
}
